import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors } from '../theme';

const ElectricalControlProduct: React.FC = () => {
  const features = [
    'Protection class IP54, IP65 or IP67',
    'Available ex stock',
    'Easy to install',
    'Flexible for modular factory automation',
    'Decentralized field power supply',
    'Developed for diverse requirements',
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.1) 100%)',
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Chip
              label="Products"
              component={Link}
              to="/products"
              clickable
              sx={{
                mb: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              Electrical Control Product
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              FIEPOS - Decentralized field power supplies for modular automation
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)` }} />

      {/* Main Content Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/electrical.jpg"
                  alt="Electrical Control Product"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                PULS Power Supplies
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 3,
                  mt: 1,
                }}
              >
                FIEPOS
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                With FIEPOS, short for <strong>FIE</strong>ld <strong>PO</strong>wer <strong>S</strong>upply, PULS presents a new type of power supply that was developed precisely for this environment: <strong>A decentralized field power supply</strong> with protection class IP54, IP65 or IP67 that is available ex stock, is easy to install and flexible enough to meet the diverse requirements of modular factory automation.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
              >
                Request a Quote
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
            >
              Why Choose FIEPOS
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: colors.secondary,
                mt: 1,
              }}
            >
              Key Features
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: colors.border,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: colors.primary,
                      boxShadow: `0 12px 40px rgba(245, 166, 35, 0.15)`,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleOutlineIcon sx={{ color: colors.primary, fontSize: 28, mt: 0.25 }} />
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.secondary,
                        fontWeight: 500,
                      }}
                    >
                      {feature}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.secondary,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: '#FFFFFF',
                mb: 3,
              }}
            >
              Interested in FIEPOS Power Supplies?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '1.1rem',
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Contact us today to learn more about how FIEPOS can benefit your automation projects.
            </Typography>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: colors.primary,
                '&:hover': {
                  backgroundColor: colors.primaryDark,
                },
              }}
            >
              Get More Information
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Back to Products */}
      <Box
        sx={{
          py: 4,
          background: '#FFFFFF',
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
        }}
      >
        <Button
          component={Link}
          to="/products"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIcon />}
        >
          Back to All Products
        </Button>
      </Box>
    </Box>
  );
};

export default ElectricalControlProduct;
